// =====================================================
// Email Service for Pet Grooming CRM
// Supports: SendGrid, Resend, and EmailJS
// =====================================================

// Choose your email provider: 'sendgrid', 'resend', or 'emailjs'
const EMAIL_PROVIDER = process.env.REACT_APP_EMAIL_PROVIDER || 'sendgrid';

// =====================================================
// SENDGRID CONFIGURATION
// =====================================================
const SENDGRID_API_KEY = process.env.REACT_APP_SENDGRID_API_KEY;
const SENDGRID_FROM_EMAIL = process.env.REACT_APP_SENDGRID_FROM_EMAIL || 'noreply@yourdomain.com';
const SENDGRID_FROM_NAME = process.env.REACT_APP_SENDGRID_FROM_NAME || 'Pet Grooming CRM';

// =====================================================
// RESEND CONFIGURATION
// =====================================================
const RESEND_API_KEY = process.env.REACT_APP_RESEND_API_KEY;
const RESEND_FROM_EMAIL = process.env.REACT_APP_RESEND_FROM_EMAIL || 'noreply@yourdomain.com';

// =====================================================
// EMAILJS CONFIGURATION
// =====================================================
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

// =====================================================
// EMAIL HTML FORMATTER (defined first)
// =====================================================
const convertLineBreaksToHTML = (text) => {
  if (!text) return '';
  
  // Convert double line breaks to paragraph breaks
  // Convert single line breaks to <br> tags
  return text
    .split('\n\n')
    .map(paragraph => {
      // Within each paragraph, convert single \n to <br>
      const formatted = paragraph.replace(/\n/g, '<br>');
      return `<p>${formatted}</p>`;
    })
    .join('');
};

const formatEmailHTML = (emailData, customTemplate = null) => {
  // Convert line breaks in the message to HTML
  const formattedMessage = convertLineBreaksToHTML(emailData.message);
  
  // If custom template exists, use it
  if (customTemplate) {
    return customTemplate
      .replace(/{{companyName}}/g, emailData.companyName || 'Pet Grooming')
      .replace(/{{adminEmail}}/g, emailData.adminEmail || '')
      .replace(/{{{message}}}/g, formattedMessage);
  }
  
  // Otherwise use default template
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <style>
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          line-height: 1.6;
          color: #333;
          max-width: 600px;
          margin: 0 auto;
          padding: 20px;
        }
        .header {
          background: linear-gradient(135deg, #9333ea 0%, #3b82f6 100%);
          color: white;
          padding: 30px;
          border-radius: 10px 10px 0 0;
          text-align: center;
        }
        .content {
          background: #ffffff;
          padding: 30px;
          border: 1px solid #e5e7eb;
          border-top: none;
        }
        .footer {
          background: #f9fafb;
          padding: 20px;
          border: 1px solid #e5e7eb;
          border-top: none;
          border-radius: 0 0 10px 10px;
          text-align: center;
          font-size: 14px;
          color: #6b7280;
        }
        .button {
          display: inline-block;
          background: linear-gradient(135deg, #9333ea 0%, #3b82f6 100%);
          color: white;
          padding: 12px 30px;
          text-decoration: none;
          border-radius: 8px;
          margin: 20px 0;
        }
        .appointment-details {
          background: #f3f4f6;
          padding: 20px;
          border-radius: 8px;
          margin: 20px 0;
        }
        .detail-row {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          border-bottom: 1px solid #e5e7eb;
        }
        .detail-row:last-child {
          border-bottom: none;
        }
        .detail-label {
          font-weight: 600;
          color: #4b5563;
        }
        .detail-value {
          color: #1f2937;
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1 style="margin: 0; font-size: 28px;">${emailData.subject}</h1>
      </div>
      <div class="content">
        ${formattedMessage}
        
        ${emailData.service_name ? `
        <div class="appointment-details">
          <h3 style="margin-top: 0;">Appointment Details</h3>
          ${emailData.service_name ? `
          <div class="detail-row">
            <span class="detail-label">Service:</span>
            <span class="detail-value">${emailData.service_name}</span>
          </div>
          ` : ''}
          ${emailData.pet_name ? `
          <div class="detail-row">
            <span class="detail-label">Pet:</span>
            <span class="detail-value">${emailData.pet_name}</span>
          </div>
          ` : ''}
          ${emailData.date ? `
          <div class="detail-row">
            <span class="detail-label">Date:</span>
            <span class="detail-value">${emailData.date}</span>
          </div>
          ` : ''}
          ${emailData.time ? `
          <div class="detail-row">
            <span class="detail-label">Time:</span>
            <span class="detail-value">${emailData.time}</span>
          </div>
          ` : ''}
        </div>
        ` : ''}
        
        <p>If you have any questions or need to make changes, please don't hesitate to contact us.</p>
        <p>Best regards,<br><strong>The Grooming Team</strong></p>
      </div>
      <div class="footer">
        <p>This is an automated message from your Pet Grooming CRM system.</p>
        <p style="font-size: 12px; margin-top: 10px;">© ${new Date().getFullYear()} Pet Grooming CRM. All rights reserved.</p>
      </div>
    </body>
    </html>
  `;
};

// =====================================================
// SENDGRID IMPLEMENTATION
// =====================================================
const sendViaSendGrid = async (emailData) => {
  if (!SENDGRID_API_KEY) {
    console.log('SendGrid not configured. Email would be sent:', emailData);
    return { success: true, message: 'SendGrid not configured' };
  }

  const response = await fetch('https://api.sendgrid.com/v3/mail/send', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${SENDGRID_API_KEY}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      personalizations: [{
        to: [{ email: emailData.to_email, name: emailData.to_name }],
        subject: emailData.subject,
      }],
      from: {
        email: SENDGRID_FROM_EMAIL,
        name: SENDGRID_FROM_NAME,
      },
      content: [{
        type: 'text/html',
        value: formatEmailHTML(emailData, emailData.customTemplate),
      }],
    }),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`SendGrid error: ${error}`);
  }

  return { success: true, message: 'Email sent via SendGrid' };
};

// =====================================================
// RESEND IMPLEMENTATION
// =====================================================
const sendViaResend = async (emailData) => {
  if (!RESEND_API_KEY) {
    console.log('Resend not configured. Email would be sent:', emailData);
    return { success: true, message: 'Resend not configured' };
  }

  const response = await fetch('https://api.resend.com/emails', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${RESEND_API_KEY}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      from: RESEND_FROM_EMAIL,
      to: emailData.to_email,
      subject: emailData.subject,
      html: formatEmailHTML(emailData, emailData.customTemplate),
    }),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Resend error: ${error}`);
  }

  return { success: true, message: 'Email sent via Resend' };
};

// =====================================================
// EMAILJS IMPLEMENTATION
// =====================================================
const sendViaEmailJS = async (emailData) => {
  if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
    console.log('EmailJS not configured. Email would be sent:', emailData);
    return { success: true, message: 'EmailJS not configured' };
  }

  // Import EmailJS dynamically
  const emailjs = await import('@emailjs/browser');

  // Always use custom template if available, otherwise use default formatting
  const htmlMessage = emailData.customTemplate 
    ? formatEmailHTML(emailData, emailData.customTemplate)
    : formatEmailHTML(emailData); // Uses default template

  const templateParams = {
    to_email: emailData.to_email,
    to_name: emailData.to_name,
    subject: emailData.subject,
    message: htmlMessage, // Send the complete formatted HTML
  };

  await emailjs.send(
    EMAILJS_SERVICE_ID,
    EMAILJS_TEMPLATE_ID,
    templateParams,
    EMAILJS_PUBLIC_KEY
  );

  return { success: true, message: 'Email sent via EmailJS' };
};

// =====================================================
// SEND EMAIL FUNCTION (exported)
// =====================================================
export const sendEmail = async (type, emailData) => {
  try {
    console.log('Attempting to send email via:', EMAIL_PROVIDER);
    console.log('Email data:', emailData);
    
    switch (EMAIL_PROVIDER) {
      case 'sendgrid':
        return await sendViaSendGrid(emailData);
      case 'resend':
        return await sendViaResend(emailData);
      case 'emailjs':
        return await sendViaEmailJS(emailData);
      default:
        console.log('No email provider configured. Email would be sent:', emailData);
        return { success: true, message: 'Email logged (no provider configured)' };
    }
  } catch (error) {
    console.error('Error sending email:', error);
    throw error;
  }
};